import type { MetricsFrameworkPeriod } from '../types';

export const SALES_DEMO_TIME_PERIOD_KEY = 'salesDemo_timePeriod';
export const SALES_DEMO_TIME_PERIOD_DEFAULT_KEY = 'salesDemo_timePeriod_default';
export const RECENT_TIMEPERIODS_KEY = 'recent_timeperiods';

export type TimePeriodOption = { code: string; name: string };

export type StoredTimePeriod = {
  timePeriod: string;
  startDate?: string;
  endDate?: string;
};

export type RecentTimeperiod = {
  label?: string;
  code: string;
  timestamp?: number;
  startDate?: string;
  endDate?: string;
};

const FALLBACK_PERIOD: MetricsFrameworkPeriod = {
  timePeriod: 'THIS_MONTH',
  label: 'This Month',
};

export const isSharedStorageRestorableCode = (
  code: string,
  options: TimePeriodOption[],
): boolean =>
  code === 'CUSTOM_RANGE' || options.some(option => option.code === code);

export const isActiveTimePeriodSelectionValid = (
  code: string,
  options: TimePeriodOption[],
): boolean =>
  isSharedStorageRestorableCode(code, options) || code.startsWith('LAST&param=');

const toPeriod = (
  code: string,
  label: string | undefined,
  startDate: string | undefined,
  endDate: string | undefined,
  options: TimePeriodOption[],
): MetricsFrameworkPeriod => {
  const matched = options.find(option => option.code === code);
  const period: MetricsFrameworkPeriod = {
    timePeriod: code,
    label: label ?? matched?.name ?? code,
  };

  if (startDate) period.startDate = startDate;
  if (endDate) period.endDate = endDate;

  return period;
};

export const getRecentTimeperiodFromStorage = (): RecentTimeperiod | null => {
  try {
    const stored = localStorage.getItem(RECENT_TIMEPERIODS_KEY);
    if (!stored) return null;
    const arr = JSON.parse(stored);
    const first = Array.isArray(arr) && arr.length ? arr[0] : null;
    if (first && first.code) {
      return {
        code: first.code,
        label: first.label,
        startDate: first.startDate,
        endDate: first.endDate,
      };
    }
    return null;
  } catch {
    return null;
  }
};

export const getStoredTimePeriod = (dealerId: string): StoredTimePeriod | null => {
  try {
    const stored = localStorage.getItem(SALES_DEMO_TIME_PERIOD_KEY);
    if (!stored) return null;
    const data = JSON.parse(stored);
    return data[dealerId] || null;
  } catch {
    return null;
  }
};

export const storeTimePeriod = (
  dealerId: string,
  timePeriod: string,
  startDate?: string,
  endDate?: string,
): void => {
  try {
    const stored = localStorage.getItem(SALES_DEMO_TIME_PERIOD_KEY);
    const data = stored ? JSON.parse(stored) : {};
    data[dealerId] = {
      timePeriod,
      startDate,
      endDate,
    };
    localStorage.setItem(SALES_DEMO_TIME_PERIOD_KEY, JSON.stringify(data));
  } catch {
    // Ignore quota / private-mode failures; dropdown recents still persist separately.
  }
};

export const getLoginDefaultTimePeriod = (): string | null => {
  try {
    return localStorage.getItem(SALES_DEMO_TIME_PERIOD_DEFAULT_KEY) || null;
  } catch {
    return null;
  }
};

export const getInitialPeriodFromSharedStorage = (): MetricsFrameworkPeriod => {
  const recent = getRecentTimeperiodFromStorage();
  if (recent?.code) {
    return toPeriod(
      recent.code,
      recent.label,
      recent.startDate,
      recent.endDate,
      [],
    );
  }

  const loginDefault = getLoginDefaultTimePeriod();
  if (loginDefault) {
    return { timePeriod: loginDefault, label: loginDefault };
  }

  return FALLBACK_PERIOD;
};

export const arePeriodsEqual = (
  left: MetricsFrameworkPeriod,
  right: MetricsFrameworkPeriod,
): boolean =>
  left.timePeriod === right.timePeriod &&
  left.label === right.label &&
  left.startDate === right.startDate &&
  left.endDate === right.endDate;

export const resolveSharedTimePeriod = ({
  dealerId,
  options,
  dealerDefaultTimePeriod,
}: {
  dealerId: string;
  options: TimePeriodOption[];
  dealerDefaultTimePeriod?: string | null;
}): MetricsFrameworkPeriod | null => {
  const recent = getRecentTimeperiodFromStorage();
  if (
    recent?.code &&
    isSharedStorageRestorableCode(recent.code, options)
  ) {
    return toPeriod(
      recent.code,
      recent.label,
      recent.startDate,
      recent.endDate,
      options,
    );
  }

  const stored = getStoredTimePeriod(dealerId);
  if (
    stored?.timePeriod &&
    isSharedStorageRestorableCode(stored.timePeriod, options)
  ) {
    return toPeriod(
      stored.timePeriod,
      undefined,
      stored.startDate,
      stored.endDate,
      options,
    );
  }

  if (
    dealerDefaultTimePeriod &&
    options.some(option => option.code === dealerDefaultTimePeriod)
  ) {
    return toPeriod(dealerDefaultTimePeriod, undefined, undefined, undefined, options);
  }

  const loginDefault = getLoginDefaultTimePeriod();
  if (loginDefault && options.some(option => option.code === loginDefault)) {
    return toPeriod(loginDefault, undefined, undefined, undefined, options);
  }

  return null;
};

export const getFallbackPeriodFromOptions = (
  options: TimePeriodOption[],
): MetricsFrameworkPeriod | null => {
  const fallback =
    options.find(option => option.code === FALLBACK_PERIOD.timePeriod) ?? options[0];
  if (!fallback) return null;
  return { timePeriod: fallback.code, label: fallback.name };
};
